import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Shield, Loader2, ChevronRight, Sparkles } from 'lucide-react';
import AudienceSwitch from '../components/auth/AudienceSwitch';
import WorkerLoginForm from '../components/auth/WorkerLoginForm';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Valami hiba történt. Kérlek, próbáld újra.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * iter 183 — Login redesigned in macOS Sonoma style.
 *
 * - Frosted-glass central card (24px backdrop-blur, rounded-3xl, subtle inner highlight)
 * - Live aurora background — 3 large blurred gradient blobs slowly drifting
 * - Parallax: the card + aurora subtly tilt with mouse movement
 * - Apple-blue (#D97036) primary accent
 * - SF Pro-style font stack (system-ui falls back to whatever the OS provides)
 */
export default function Login() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  // iter 320 — közönség-váltó: Vállalkozás / Dolgozó (URL ?as=worker vagy utolsó választás)
  const [audience, setAudienceState] = useState(() => (new URLSearchParams(window.location.search).get('as') === 'worker' || localStorage.getItem('acx_login_audience') === 'worker') ? 'worker' : 'company');
  const setAudience = (a) => { setAudienceState(a); localStorage.setItem('acx_login_audience', a); };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const cardRef = useRef(null);

  // Parallax — tilt card + shift aurora by mouse position
  useEffect(() => {
    const wrap = wrapRef.current;
    const card = cardRef.current;
    if (!wrap) return undefined;
    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5; // -0.5 .. 0.5
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      if (card) {
        card.style.transform = `perspective(1100px) rotateY(${x * 3.5}deg) rotateX(${-y * 3.5}deg)`;
      }
      wrap.style.setProperty('--mx', `${x * 30}px`);
      wrap.style.setProperty('--my', `${y * 30}px`);
    };
    const onLeave = () => {
      if (card) card.style.transform = '';
      wrap.style.setProperty('--mx', '0px');
      wrap.style.setProperty('--my', '0px');
    };
    wrap.addEventListener('mousemove', onMove);
    wrap.addEventListener('mouseleave', onLeave);
    return () => {
      wrap.removeEventListener('mousemove', onMove);
      wrap.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'hu' ? 'sk' : 'hu';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success(t('auth.loginSuccess', 'Sikeres bejelentkezés'));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isHu = i18n.language === 'hu';

  return (
    <div
      ref={wrapRef}
      className="min-h-screen w-full relative overflow-hidden flex items-center justify-center px-4 sm:px-6"
      style={{
        backgroundColor: '#0B1220',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", system-ui, sans-serif',
      }}
      data-testid="login-page"
    >
      {/* ─────── Live aurora background (3 blobs that drift) ─────── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base radial gradient */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 30% 20%, #1A2240 0%, transparent 60%), radial-gradient(circle at 80% 80%, #5C2C14 0%, transparent 60%), radial-gradient(circle at 50% 50%, #0B0E1A 0%, #06090F 100%)',
        }} />

        {/* Aurora blob 1 — blue */}
        <div
          className="absolute rounded-full opacity-60 aurora-blob-1"
          style={{
            top: '5%', left: '8%',
            width: '60vw', height: '60vw',
            maxWidth: 900, maxHeight: 900,
            background: 'radial-gradient(circle, rgba(74,90,133,0.55) 0%, rgba(74,90,133,0) 70%)',
            filter: 'blur(80px)',
            transform: 'translate(var(--mx, 0px), var(--my, 0px))',
            transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
        {/* Aurora blob 2 — magenta */}
        <div
          className="absolute rounded-full opacity-50 aurora-blob-2"
          style={{
            bottom: '0%', right: '5%',
            width: '55vw', height: '55vw',
            maxWidth: 800, maxHeight: 800,
            background: 'radial-gradient(circle, rgba(217,112,54,0.50) 0%, rgba(217,112,54,0) 70%)',
            filter: 'blur(90px)',
            transform: 'translate(calc(var(--mx, 0px) * -1), calc(var(--my, 0px) * -1))',
            transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
        {/* Aurora blob 3 — teal */}
        <div
          className="absolute rounded-full opacity-40 aurora-blob-3"
          style={{
            top: '40%', left: '60%',
            width: '45vw', height: '45vw',
            maxWidth: 700, maxHeight: 700,
            background: 'radial-gradient(circle, rgba(232,134,83,0.30) 0%, rgba(232,134,83,0) 70%)',
            filter: 'blur(70px)',
            transform: 'translate(calc(var(--mx, 0px) * 0.5), calc(var(--my, 0px) * 0.5))',
            transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />

        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }} />
      </div>

      {/* ─────── Aurora blob animations (CSS only) ─────── */}
      <style>{`
        @keyframes drift1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(40px, -30px) scale(1.06); }
        }
        @keyframes drift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, 40px) scale(1.08); }
        }
        @keyframes drift3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -20px) scale(0.96); }
        }
        .aurora-blob-1 { animation: drift1 18s ease-in-out infinite; }
        .aurora-blob-2 { animation: drift2 22s ease-in-out infinite; }
        .aurora-blob-3 { animation: drift3 26s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .aurora-blob-1, .aurora-blob-2, .aurora-blob-3 { animation: none; }
        }

        /* Floating-label input */
        .float-field { position: relative; }
        .float-field input {
          width: 100%;
          height: 56px;
          padding: 22px 16px 8px 46px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.09);
          border: 1px solid rgba(255, 255, 255, 0.18);
          color: #FFFFFF;
          font-size: 17px;
          letter-spacing: -0.01em;
          transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        .float-field input:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.16);
        }
        .float-field input:focus {
          outline: none;
          border-color: rgba(217,112,54, 0.65);
          background: rgba(255, 255, 255, 0.10);
          box-shadow: 0 0 0 4px rgba(217,112,54, 0.15);
        }
        .float-field .label {
          position: absolute;
          left: 46px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(248, 250, 252, 0.8);
          font-size: 16px;
          pointer-events: none;
          transition: transform 0.2s ease, font-size 0.2s ease, color 0.2s ease;
        }
        .float-field input:focus + .label,
        .float-field input:not(:placeholder-shown) + .label {
          transform: translateY(-125%);
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #F5B48A;
        }
        .float-field .icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(248, 250, 252, 0.7);
          pointer-events: none;
        }
        .float-field input:focus ~ .icon { color: rgba(217,112,54, 0.85); }
        .float-field .toggle {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(248, 250, 252, 0.8);
          border-radius: 9px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }
        .float-field .toggle:hover { background: rgba(255, 255, 255, 0.07); color: #F8FAFC; }
      `}</style>

      {/* ─────── Marketing tagline (top of the form area) ─────── */}
      <div className="absolute top-12 left-0 right-0 z-10 text-center px-4 pointer-events-none hidden md:block">
        <p
          className="font-semibold mb-1.5"
          style={{
            color: '#FFFFFF',
            fontSize: 'clamp(26px, 2.9vw, 40px)',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            textShadow: '0 2px 30px rgba(0,0,0,0.4)',
          }}
        >
          {isHu ? 'Számlák, ajánlatok, ügyfelek — egy helyen.' : 'Faktúry, ponuky, klienti — na jednom mieste.'}
        </p>
        <p style={{ color: 'rgba(248,250,252,0.9)', fontSize: 17, letterSpacing: '-0.01em' }}>
          {isHu
            ? 'Az Acountix az új könyvelési alkalmazás vállalkozóknak.'
            : 'Acountix je nová účtovná aplikácia pre podnikateľov.'}
        </p>
      </div>

      {/* ─────── LEFT: MacBook mockup with real proposal-creation screenshot ─────── */}
      <div
        className="absolute z-[5] hidden lg:block pointer-events-none device-float-mac"
        style={{
          left: '3vw',
          top: '50%',
          width: 'min(28vw, 380px)',
          transform: 'translateY(-50%)',
        }}
        aria-hidden="true"
      >
        <div
          style={{
            background: 'linear-gradient(180deg, #1A1F2A 0%, #0F141C 100%)',
            borderRadius: '14px 14px 4px 4px',
            padding: '10px 10px 6px 10px',
            boxShadow: '0 40px 90px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div className="mx-auto mb-1.5 rounded-full" style={{ width: 4, height: 4, background: '#0A0E14', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)' }} />
          {/* Real screenshot of the proposal page */}
          <div
            style={{
              borderRadius: 6,
              overflow: 'hidden',
              aspectRatio: '16 / 10',
              background: '#F8FAFC',
              position: 'relative',
            }}
          >
            <img
              src="/login/proposal-shot.png"
              alt="Acountix proposal creation"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top left',
              }}
            />
            {/* Animated typing cursor + AI badge overlay */}
            <div
              className="absolute"
              style={{
                top: '8%',
                right: '8%',
                padding: '4px 8px',
                background: 'linear-gradient(135deg, #D97036, #B85A26)',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.5)',
                animation: 'mac-ai-pulse 2.4s ease-in-out infinite',
                fontSize: 8,
                color: '#FFFFFF',
                fontWeight: 700,
                letterSpacing: '0.05em',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <span style={{ width: 4, height: 4, background: '#FFFFFF', borderRadius: 1, animation: 'mac-dot-blink 0.8s steps(2) infinite' }} />
              AI ÍR…
            </div>
            {/* Pulse around the SAVE button area */}
            <div
              className="absolute"
              style={{
                bottom: '15%',
                left: '38%',
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: '2px solid #D97036',
                animation: 'mac-pulse-ring 2s ease-out infinite',
                opacity: 0,
              }}
            />
            {/* Soft sheen sweep */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 45%, transparent 60%)',
                animation: 'screen-sheen 7s ease-in-out infinite',
              }}
            />
          </div>
          {/* Hinge */}
          <div style={{ marginTop: 4, marginInline: -10, height: 5, background: 'linear-gradient(180deg, #20262F 0%, #0E121A 100%)', borderRadius: '0 0 4px 4px' }} />
          <div className="mx-auto" style={{ width: 50, height: 1.5, background: '#0A0E14', borderRadius: 1, marginTop: -3 }} />
        </div>
        {/* Caption */}
        <p className="text-center mt-4" style={{ color: '#FFFFFF', fontSize: 16, letterSpacing: '-0.005em', fontWeight: 600 }}>
          {isHu ? 'Élő PDF-előnézet és AI-varázsló' : 'Živý PDF náhľad a AI sprievodca'}
        </p>
      </div>

      {/* ─────── RIGHT: TWO iPhones with different screens ─────── */}
      <div
        className="absolute z-[5] hidden lg:block pointer-events-none"
        style={{
          right: '3vw',
          top: '50%',
          width: 'min(28vw, 380px)',
          transform: 'translateY(-50%)',
        }}
        aria-hidden="true"
      >
        <div className="relative" style={{ height: 420 }}>
          {/* Phone 1 — back, dashboard */}
          <div
            className="absolute device-float-phone-back"
            style={{
              left: '0%',
              top: '8%',
              width: 175,
              transform: 'rotate(-5deg)',
              filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.55))',
              zIndex: 1,
            }}
          >
            <div
              style={{
                background: 'linear-gradient(180deg, #1A1F2A 0%, #0F141C 100%)',
                borderRadius: 30,
                padding: 6,
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div
                style={{
                  background: '#FFFFFF',
                  borderRadius: 24,
                  aspectRatio: '9 / 19',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/login/mobile-shot.png"
                  alt="Dashboard"
                  draggable={false}
                  className="phone-scroll-img"
                  style={{ width: '100%', height: 'auto', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                />
                <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 7, width: 50, height: 13, background: '#000', borderRadius: 6.5, zIndex: 2 }} />
                {/* Notification badge pulse */}
                <div
                  className="absolute"
                  style={{
                    top: 25, right: 12, width: 6, height: 6,
                    background: '#FF453A', borderRadius: '50%',
                    animation: 'phone-notif-pulse 2s ease-out infinite',
                    zIndex: 3,
                  }}
                />
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2" style={{ width: 44, height: 2, background: 'rgba(0,0,0,0.5)', borderRadius: 1, zIndex: 2 }} />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.18) 45%, transparent 60%)', animation: 'screen-sheen 6s ease-in-out infinite 1.5s' }}
                />
              </div>
            </div>
          </div>

          {/* Phone 2 — front, invoices */}
          <div
            className="absolute device-float-phone-front"
            style={{
              right: '0%',
              top: '0%',
              width: 195,
              transform: 'rotate(6deg)',
              filter: 'drop-shadow(0 35px 70px rgba(0,0,0,0.65))',
              zIndex: 2,
            }}
          >
            <div
              style={{
                background: 'linear-gradient(180deg, #1A1F2A 0%, #0F141C 100%)',
                borderRadius: 32,
                padding: 7,
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div
                style={{
                  background: '#FFFFFF',
                  borderRadius: 26,
                  aspectRatio: '9 / 19',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/login/mobile-shot-2.png"
                  alt="Invoices"
                  draggable={false}
                  className="phone-scroll-img-2"
                  style={{ width: '100%', height: 'auto', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                />
                <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 8, width: 56, height: 14, background: '#000', borderRadius: 7, zIndex: 2 }} />
                {/* Live status dot (green) — "paid" notification */}
                <div
                  className="absolute flex items-center gap-1.5"
                  style={{
                    top: 26, right: 11,
                    padding: '3px 6px',
                    background: 'rgba(52,199,89,0.18)',
                    border: '1px solid rgba(52,199,89,0.5)',
                    borderRadius: 6,
                    backdropFilter: 'blur(6px)',
                    animation: 'phone-paid-pulse 2.6s ease-in-out infinite',
                    zIndex: 3,
                  }}
                >
                  <span style={{ width: 5, height: 5, background: '#34C759', borderRadius: '50%', boxShadow: '0 0 6px #34C759' }} />
                  <span style={{ fontSize: 7, fontWeight: 700, color: '#34C759', letterSpacing: '0.03em' }}>FIZETVE</span>
                </div>
                <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2" style={{ width: 50, height: 2, background: 'rgba(0,0,0,0.5)', borderRadius: 1, zIndex: 2 }} />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.20) 45%, transparent 60%)', animation: 'screen-sheen 6.5s ease-in-out infinite 0.5s' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <p className="text-center mt-4" style={{ color: '#FFFFFF', fontSize: 16, letterSpacing: '-0.005em', fontWeight: 600 }}>
          {isHu ? 'Dashboard és számlák — a zsebedben' : 'Dashboard a faktúry — vo vrecku'}
        </p>
      </div>

      {/* Device animations CSS */}
      <style>{`
        @keyframes device-float-mac {
          0%, 100% { transform: translateY(-50%); }
          50% { transform: translateY(calc(-50% - 10px)); }
        }
        @keyframes device-float-phone {
          0%, 100% { transform: translateY(-50%) rotate(3deg); }
          50% { transform: translateY(calc(-50% + 12px)) rotate(3.4deg); }
        }
        .device-float-mac { animation: device-float-mac 7s ease-in-out infinite; }
        .device-float-phone { animation: device-float-phone 6.5s ease-in-out infinite; }

        /* iter 179 — AI badge tile animations */
        @keyframes ai-tile-sheen {
          0% { transform: translateX(-120%); }
          60% { transform: translateX(120%); }
          100% { transform: translateX(120%); }
        }
        .ai-tile-sheen { animation: ai-tile-sheen 4.8s ease-in-out infinite; }
        @keyframes ai-tile-ring {
          0% { transform: scale(0.85); opacity: 0.7; }
          70% { transform: scale(1.18); opacity: 0; }
          100% { transform: scale(1.18); opacity: 0; }
        }
        .ai-tile-ring { animation: ai-tile-ring 2.4s ease-out infinite; }
        @keyframes ai-tile-dot {
          0%, 100% { transform: translateY(0); opacity: 0.85; }
          50% { transform: translateY(-3px); opacity: 1; }
        }
        .ai-tile-dot { animation: ai-tile-dot 2.6s ease-in-out infinite; }
        .ai-tile-dot-2 { animation-delay: 0.9s; }
        @keyframes ai-tile-typing {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 1; }
        }
        .ai-tile-typing { animation: ai-tile-typing 1.2s steps(3) infinite; }
        .ai-badge-tile:hover .ai-tile-sheen { animation-duration: 2.2s; }

        @keyframes mac-ai-pulse {
          0%, 100% { box-shadow: 0 4px 12px rgba(124, 58, 237, 0.5); transform: scale(1); }
          50% { box-shadow: 0 6px 18px rgba(124, 58, 237, 0.75); transform: scale(1.04); }
        }
        @keyframes mac-dot-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0.25; }
        }
        @keyframes mac-pulse-ring {
          0% { opacity: 0.8; transform: scale(0.6); }
          70% { opacity: 0; transform: scale(2.0); }
          100% { opacity: 0; transform: scale(2.0); }
        }
        @keyframes screen-sheen {
          0%, 40%, 100% { transform: translateX(-120%); opacity: 0; }
          50% { opacity: 0.7; }
          70% { transform: translateX(120%); opacity: 0; }
        }
        @keyframes phone-notif-pulse {
          0% { box-shadow: 0 0 0 0 rgba(255,69,58,0.65); }
          70% { box-shadow: 0 0 0 10px rgba(255,69,58,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,69,58,0); }
        }
        @keyframes phone-scroll {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-22%); }
        }
        .phone-scroll-img { animation: phone-scroll 12s ease-in-out infinite; }

        @media (prefers-reduced-motion: reduce) {
          .device-float-mac, .device-float-phone,
          .phone-scroll-img { animation: none !important; }
        }
      `}</style>

      {/* ─────── Top-bar: brand + language toggle ─────── */}
      <div className="absolute top-5 left-0 right-0 z-20 flex items-center justify-between px-5 sm:px-8">
        <div className="flex items-center gap-2.5">
          <div className="relative w-9 h-9 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.04))',
              border: '1px solid rgba(255,255,255,0.18)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
            }}
          >
            <Shield size={18} color="#FFFFFF" strokeWidth={2} />
          </div>
          <span
            className="font-semibold"
            style={{ color: '#FFFFFF', fontSize: 17, letterSpacing: '-0.01em' }}
          >
            Acountix
          </span>
        </div>
        <button
          onClick={toggleLanguage}
          data-testid="login-language-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
          style={{
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: '#F8FAFC',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
        >
          {i18n.language === 'hu' ? '🇭🇺 HU' : '🇸🇰 SK'}
        </button>
      </div>

      {/* ─────── Center column: card + AI tile (vertical stack) ─────── */}
      <div className="relative z-10 w-full flex flex-col items-center pt-20 md:pt-24 pb-16" data-testid="login-center-col">

      {/* ─────── Glass card ─────── */}
      <div
        ref={cardRef}
        data-testid="login-card"
        className="relative z-10 w-full max-w-md rounded-[28px] p-7 sm:p-9 transition-transform duration-150"
        style={{
          background: 'linear-gradient(140deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 100%)',
          border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 1px 0 rgba(255,255,255,0.10) inset',
          backdropFilter: 'blur(28px) saturate(160%)',
          WebkitBackdropFilter: 'blur(28px) saturate(160%)',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Card top highlight */}
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-[28px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }} />

        {/* iter 320 — Ki vagy? Vállalkozás / Dolgozó */}
        <div className="mb-6"><AudienceSwitch value={audience} onChange={setAudience} isHu={isHu} /></div>

        {/* Heading — animated greeting (iter 184) */}
        <div className="mb-6" key={audience}>
          {(() => {
            const text = audience === 'worker' ? (isHu ? 'Dolgozói belépés' : 'Prihlásenie zamestnanca') : (isHu ? 'Vállalkozói belépés' : 'Prihlásenie firmy');
            const chars = Array.from(text);
            return (
              <h1
                aria-label={text}
                className="font-semibold mb-1.5"
                style={{
                  color: '#FFFFFF',
                  fontSize: 'clamp(28px, 7.5vw, 34px)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.1,
                  display: 'inline-flex',
                  flexWrap: 'wrap',
                }}
              >
                {chars.map((ch, i) => (
                  <span
                    key={i}
                    aria-hidden="true"
                    className="greet-char"
                    style={{
                      display: 'inline-block',
                      whiteSpace: 'pre',
                      animationDelay: `${i * 55}ms`,
                      background: 'linear-gradient(90deg, #FFFFFF 0%, #F0A070 50%, #FFFFFF 100%)',
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {ch === ' ' ? '\u00A0' : ch}
                  </span>
                ))}
              </h1>
            );
          })()}
          <p style={{ color: '#FFFFFF', fontSize: 17, letterSpacing: '-0.01em', lineHeight: 1.45 }}>
            {audience === 'worker'
              ? (isHu ? 'Jelenlét, feladatok, beosztás — a telefonodon. Egy fiók, bármelyik céghez.' : 'Dochádzka, úlohy, zmeny — v telefóne. Jeden účet pre každú firmu.')
              : (isHu ? 'Cégvezetőknek és könyvelőknek — jelentkezz be a vállalkozásod fiókjába.' : 'Pre majiteľov a účtovníkov — prihláste sa do účtu vašej firmy.')}
          </p>
        </div>

        {/* Greeting animation CSS */}
        <style>{`
          @keyframes greet-pop {
            0% {
              opacity: 0;
              transform: translateY(14px) scale(0.85);
              filter: blur(6px);
            }
            55% {
              opacity: 1;
              transform: translateY(-3px) scale(1.05);
              filter: blur(0px);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0px);
            }
          }
          @keyframes greet-shimmer {
            0%, 100% { background-position: 200% 0; }
            50% { background-position: -200% 0; }
          }
          .greet-char {
            opacity: 0;
            animation:
              greet-pop 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards,
              greet-shimmer 5s linear infinite;
            animation-delay: var(--d, 0ms), 1.5s;
          }
          @media (prefers-reduced-motion: reduce) {
            .greet-char {
              opacity: 1;
              animation: none;
            }
          }
        `}</style>

        {/* Error banner */}
        {error && audience === 'company' && (
          <div
            data-testid="login-error"
            className="mb-4 px-3.5 py-2.5 rounded-xl flex items-start gap-2.5"
            style={{
              background: 'rgba(255, 69, 58, 0.12)',
              border: '1px solid rgba(255, 69, 58, 0.30)',
              color: '#FFC9C4',
              fontSize: 15,
              letterSpacing: '-0.01em',
            }}
          >
            <div className="w-4 h-4 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255, 69, 58, 0.35)' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>!</span>
            </div>
            <span>{error}</span>
          </div>
        )}

        {audience === 'worker' && <WorkerLoginForm registerTo="/dolgozo?tab=register" />}

        {/* Form */}
        {audience === 'company' && <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Email */}
          <div className="float-field">
            <input
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder=" "
              autoComplete="email"
              required
            />
            <span className="label">Email</span>
            <Mail size={18} strokeWidth={1.8} className="icon" />
          </div>

          {/* Password */}
          <div className="float-field">
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder=" "
              autoComplete="current-password"
              required
            />
            <span className="label">{isHu ? 'Jelszó' : 'Heslo'}</span>
            <Lock size={18} strokeWidth={1.8} className="icon" />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((p) => !p)}
              data-testid="login-toggle-password"
              className="toggle"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          {/* Remember + forgot */}
          <div className="flex items-center justify-between pt-1 pb-1.5">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <span
                className="relative inline-flex items-center justify-center rounded-md transition-all"
                style={{
                  width: 18, height: 18,
                  background: remember ? '#D97036' : 'rgba(255,255,255,0.10)',
                  border: `1px solid ${remember ? '#D97036' : 'rgba(255,255,255,0.22)'}`,
                  boxShadow: remember ? '0 0 0 4px rgba(217,112,54,0.15)' : 'none',
                }}
              >
                {remember && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="login-remember"
                className="sr-only"
              />
              <span style={{ color: '#FFFFFF', fontSize: 16 }}>
                {isHu ? 'Maradjak bejelentkezve' : 'Zostať prihlásený'}
              </span>
            </label>
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              style={{ color: '#F5B48A', fontSize: 16, fontWeight: 600 }}
              className="hover:underline"
            >
              {isHu ? 'Elfelejtetted?' : 'Zabudli ste?'}
            </Link>
          </div>

          {/* Primary button — Apple blue */}
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              height: 52,
              borderRadius: 14,
              background: 'linear-gradient(180deg, #D97036 0%, #B85A26 100%)',
              color: '#FFFFFF',
              fontSize: 17,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              boxShadow: '0 8px 24px -4px rgba(184,90,38,0.5), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.15)',
              transition: 'transform 0.1s ease, box-shadow 0.2s ease',
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.985)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
          >
            {loading ? (
              <>
                <Loader2 size={17} className="animate-spin" />
                <span>{isHu ? 'Bejelentkezés…' : 'Prihlasujem…'}</span>
              </>
            ) : (
              <>
                <span>{isHu ? 'Bejelentkezés' : 'Prihlásiť sa'}</span>
                <ArrowRight size={17} strokeWidth={2.25} />
              </>
            )}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.18)' }} />
            <span style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 700, letterSpacing: '0.08em' }}>
              {isHu ? 'VAGY' : 'ALEBO'}
            </span>
            <span className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.18)' }} />
          </div>

          {/* Register CTA — secondary glass button */}
          <Link
            to="/register"
            data-testid="goto-register"
            className="w-full flex items-center justify-center gap-2 rounded-2xl transition-all"
            style={{
              height: 50,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.22)',
              color: '#FFFFFF',
              fontSize: 16.5,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
          >
            <span>{isHu ? 'Nincs még fiókod? Regisztrálj' : 'Nemáte účet? Registrujte sa'}</span>
            <ChevronRight size={16} strokeWidth={2.25} />
          </Link>

        </form>}

        {/* Footer — tiny shield admin link */}
        <div className="mt-7 pt-5 flex items-center justify-center" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Link
            to="/admin"
            data-testid="login-admin-link"
            aria-label="Admin"
            className="opacity-30 hover:opacity-70 transition-opacity"
          >
            <Shield size={14} color="#FFFFFF" strokeWidth={1.5} />
          </Link>
        </div>
      </div>

      {/* ─────── Standalone AI tile (below login card) — iter 179 ─────── */}
      <div
        className="relative z-10 mx-auto w-full max-w-[420px] mt-5 px-4 sm:px-0"
        data-testid="ai-tile-wrapper"
      >
        <div
          data-testid="ai-badge-tile"
          className="ai-badge-tile relative overflow-hidden rounded-2xl"
          style={{
            padding: '14px 16px',
            background: 'linear-gradient(120deg, rgba(124,58,237,0.22) 0%, rgba(59,130,246,0.18) 50%, rgba(16,185,129,0.20) 100%)',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 12px 32px -10px rgba(124,58,237,0.45)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}
        >
          {/* Animated shimmer sweep */}
          <span
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none ai-tile-sheen"
            style={{
              background: 'linear-gradient(115deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)',
            }}
          />
          {/* Floating tiny "AI dots" */}
          <span aria-hidden="true" className="absolute ai-tile-dot" style={{ top: 9, right: 16, width: 4, height: 4, borderRadius: 4, background: '#A78BFA', boxShadow: '0 0 8px #A78BFA' }} />
          <span aria-hidden="true" className="absolute ai-tile-dot ai-tile-dot-2" style={{ bottom: 11, right: 36, width: 3, height: 3, borderRadius: 3, background: '#60A5FA', boxShadow: '0 0 6px #60A5FA' }} />

          <div className="relative flex items-center gap-3">
            {/* Icon with pulse ring */}
            <div className="relative flex-shrink-0" style={{ width: 40, height: 40 }}>
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full ai-tile-ring"
                style={{ border: '1.5px solid rgba(167,139,250,0.55)' }}
              />
              <div
                className="absolute inset-1 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #E88653 0%, #94461C 100%)',
                  boxShadow: '0 4px 12px rgba(124,58,237,0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
                }}
              >
                <Sparkles size={17} color="#FFFFFF" strokeWidth={2.2} />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div
                className="flex items-center gap-1.5"
                style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', color: '#D8CCFF' }}
              >
                <span>{isHu ? 'AI MEGHAJTÁSSAL' : 'POHÁŇANÉ AI'}</span>
                <span className="ai-tile-typing" aria-hidden="true">…</span>
              </div>
              <p
                className="mt-1 truncate"
                style={{
                  color: '#FFFFFF',
                  fontSize: 15.5,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.25,
                }}
              >
                {isHu ? 'Élő AI varázsló' : 'Živý AI sprievodca'}
              </p>
            </div>
          </div>
        </div>

        {/* Caption BELOW tile (slogan) */}
        <p
          className="text-center mt-3 px-2"
          style={{
            color: '#FFFFFF',
            fontSize: 15,
            fontWeight: 500,
            letterSpacing: '-0.005em',
            lineHeight: 1.45,
          }}
        >
          {isHu
            ? 'AI dolgozik a háttérben — érted, helyetted.'
            : 'AI pracuje v pozadí — za teba, pre teba.'}
        </p>
      </div>

      </div>{/* end center column */}

      {/* ─────── Bottom copyright ─────── */}
      <div className="absolute bottom-5 left-0 right-0 text-center px-4">
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14.5, letterSpacing: '-0.005em' }}>
          © {new Date().getFullYear()} Acountix · {isHu ? 'Adatvédelem és biztonság előtérben' : 'Súkromie a bezpečnosť na prvom mieste'}
        </p>
      </div>
    </div>
  );
}
